import { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  Mail, Phone, Building2, FileText, Download, ExternalLink, ArrowLeft,
  Loader2, Inbox, Calendar, Hash, MessageSquare,
} from 'lucide-react';
import './ContactSubmissionDetail.css';

function authFetch(url, options = {}) {
  const token = localStorage.getItem('ozel_token');
  return fetch(url, {
    ...options,
    headers: { ...(options.headers || {}), ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    credentials: 'include',
  });
}

const TYPE_LABEL = { werkgever: 'Ik zoek personeel', werknemer: 'Ik zoek werk' };

function formatDate(iso) {
  try {
    return new Date(iso).toLocaleString('nl-NL', {
      day: '2-digit', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit',
    });
  } catch { return iso; }
}

function formatSize(bytes) {
  const n = Number(bytes) || 0;
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

// Telefoonnummer -> WhatsApp-nummer (alleen cijfers, NL-nummers naar landcode 31).
function waNumber(phone) {
  let d = String(phone || '').replace(/\D/g, '');
  if (!d) return '';
  if (d.startsWith('00')) d = d.slice(2);
  else if (d.startsWith('0')) d = `31${d.slice(1)}`;
  return d;
}

// In een standalone PWA is een gewone download-link onbetrouwbaar (blanco scherm
// of opent de systeembrowser). Daarom het bestand als blob ophalen en de download
// programmatisch starten; werkt op desktop, mobiel en in de PWA.
async function downloadFile(file) {
  const res = await authFetch(`/api/contact-files/${file.id}?download=1`);
  if (!res.ok) throw new Error(`Download mislukt (${res.status})`);
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = file.original_name || `bijlage-${file.id}`;
  a.rel = 'noopener';
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 10000);
}

const isImage = (m) => /^image\//.test(String(m || ''));
const isPdf = (m) => String(m || '') === 'application/pdf';

export default function ContactSubmissionDetail() {
  const { id } = useParams();
  const [item, setItem] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [busyFile, setBusyFile] = useState(null);
  const [dlError, setDlError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await authFetch(`/api/contact-submissions/${id}`);
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        throw new Error(d.error || `Kon inschrijving niet laden (${res.status})`);
      }
      setItem(await res.json());
    } catch (err) {
      setError(err.message || 'Kon inschrijving niet laden');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  // Openen telt als gelezen.
  useEffect(() => {
    if (!item || item.is_read) return;
    authFetch(`/api/contact-submissions/${id}/read`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ is_read: true }),
    }).catch(() => {});
  }, [item, id]);

  if (loading) {
    return <div className="csd-state"><Loader2 size={20} className="csd-spin" /> Aanvraag laden…</div>;
  }
  if (error) {
    return (
      <div className="csd-state csd-state-error">
        <Inbox size={26} />
        <p>{error}</p>
        <button className="csd-btn" onClick={load}>Opnieuw proberen</button>
      </div>
    );
  }

  const wa = waNumber(item.phone);
  const kvk = (item.files || []).filter((f) => f.category === 'kvk');
  const certs = (item.files || []).filter((f) => f.category !== 'kvk');

  const renderFile = (f) => (
    <div className="csd-file" key={f.id}>
      <div className="csd-file-preview">
        {isImage(f.mime_type) ? (
          <a href={`/api/contact-files/${f.id}`} target="_blank" rel="noopener noreferrer">
            <img src={`/api/contact-files/${f.id}`} alt={f.original_name || 'bijlage'} loading="lazy" />
          </a>
        ) : isPdf(f.mime_type) ? (
          <object data={`/api/contact-files/${f.id}#toolbar=0&navpanes=0`} type="application/pdf">
            <div className="csd-file-fallback"><FileText size={26} /><span>PDF — open om te bekijken</span></div>
          </object>
        ) : (
          <div className="csd-file-fallback"><FileText size={26} /><span>Geen voorbeeld</span></div>
        )}
      </div>

      <div className="csd-file-info">
        <span className="csd-file-name" title={f.original_name}>{f.original_name || 'bestand'}</span>
        <span className="csd-file-meta">{formatSize(f.size_bytes)}</span>
      </div>

      <div className="csd-file-actions">
        <a className="csd-btn csd-btn-sm" href={`/api/contact-files/${f.id}`} target="_blank" rel="noopener noreferrer">
          <ExternalLink size={14} /> Openen
        </a>
        <button
          type="button"
          className="csd-btn csd-btn-sm csd-btn-primary"
          disabled={busyFile === f.id}
          onClick={async () => {
            setBusyFile(f.id);
            setDlError('');
            try { await downloadFile(f); }
            catch (err) { setDlError(err.message || 'Download mislukt'); }
            finally { setBusyFile(null); }
          }}
        >
          {busyFile === f.id ? <Loader2 size={14} className="csd-spin" /> : <Download size={14} />}
          {busyFile === f.id ? 'Bezig…' : 'Download'}
        </button>
      </div>
    </div>
  );

  return (
    <div className="csd">
      <div className="csd-topbar">
        <Link className="csd-back" to="/dashboard/aanvragen"><ArrowLeft size={16} /> Inschrijvingen</Link>
        <span className={`csd-badge ${item.type}`}>{TYPE_LABEL[item.type] || item.type}</span>
      </div>

      <header className="csd-head">
        <h1>{item.name}</h1>
        <p className="csd-when"><Calendar size={14} /> Ontvangen op {formatDate(item.created_at)}</p>
      </header>

      {/* Snel contact opnemen */}
      <div className="csd-actions">
        <a className="csd-btn csd-btn-primary" href={`mailto:${item.email}`}>
          <Mail size={15} /> E-mail sturen
        </a>
        {item.phone && (
          <a className="csd-btn" href={`tel:${item.phone}`}>
            <Phone size={15} /> Bellen
          </a>
        )}
        {wa && (
          <a className="csd-btn csd-btn-wa" href={`https://wa.me/${wa}`} target="_blank" rel="noopener noreferrer">
            <svg viewBox="0 0 24 24" fill="currentColor" width="15" height="15" aria-hidden="true">
              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z" />
              <path d="M12 0C5.373 0 0 5.373 0 12c0 2.123.554 4.117 1.528 5.845L0 24l6.335-1.502A11.954 11.954 0 0012 24c6.627 0 12-5.373 12-12S18.627 0 12 0zm0 21.818a9.818 9.818 0 01-5.017-1.374l-.36-.214-3.732.885.936-3.619-.234-.373A9.818 9.818 0 1112 21.818z" />
            </svg>
            WhatsApp
          </a>
        )}
      </div>

      {/* Gegevens */}
      <section className="csd-card">
        <h2>Gegevens</h2>
        <dl className="csd-grid">
          <div className="csd-row">
            <dt><Mail size={14} /> E-mail</dt>
            <dd><a href={`mailto:${item.email}`}>{item.email}</a></dd>
          </div>
          {item.phone && (
            <div className="csd-row">
              <dt><Phone size={14} /> Telefoon</dt>
              <dd>
                <a href={`tel:${item.phone}`}>{item.phone}</a>
                {wa && (
                  <a className="csd-wa-inline" href={`https://wa.me/${wa}`} target="_blank" rel="noopener noreferrer">
                    WhatsApp
                  </a>
                )}
              </dd>
            </div>
          )}
          {item.company && (
            <div className="csd-row">
              <dt><Building2 size={14} /> Bedrijf</dt>
              <dd>{item.company}</dd>
            </div>
          )}
          {item.btw_nummer && (
            <div className="csd-row">
              <dt><Hash size={14} /> BTW-nummer</dt>
              <dd>{item.btw_nummer}</dd>
            </div>
          )}
          {item.subject && (
            <div className="csd-row">
              <dt><MessageSquare size={14} /> Onderwerp</dt>
              <dd>{item.subject}</dd>
            </div>
          )}
        </dl>
      </section>

      {/* Bericht */}
      <section className="csd-card">
        <h2>Bericht</h2>
        <p className="csd-message">{item.message}</p>
      </section>

      {/* Bijlagen */}
      {(item.files || []).length > 0 && (
        <section className="csd-card">
          <h2>Bijlagen ({item.files.length})</h2>
          {dlError && <div className="csd-dl-error">{dlError}</div>}

          {kvk.length > 0 && (
            <>
              <h3 className="csd-subhead">KvK-uittreksel</h3>
              <div className="csd-files">{kvk.map(renderFile)}</div>
            </>
          )}
          {certs.length > 0 && (
            <>
              <h3 className="csd-subhead">Certificeringen</h3>
              <div className="csd-files">{certs.map(renderFile)}</div>
            </>
          )}
        </section>
      )}
    </div>
  );
}
